import { useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function yn(v) {
  if (v === null || v === undefined) return 'unknown (not checked)';
  return v ? 'yes' : 'no';
}

export function ContactsCollisionCheckPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillApplyResult, setBackfillApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.contactsCollision();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runBackfillPreview = async () => {
    setPreviewLoading(true);
    setBackfillPreview(null);
    setBackfillApplyResult(null);
    try {
      const { data } = await diagnosticsAPI.contactsCollisionBackfillPreview();
      setBackfillPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runBackfillApply = async () => {
    setApplyLoading(true);
    try {
      const { data } = await diagnosticsAPI.contactsCollisionBackfillApply();
      setBackfillApplyResult(data);
      toast.success(`Stamped ${data.stamped_total} contact doc(s)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyLoading(false);
    }
  };

  const r = result;
  const overlaps = (r && r.overlaps) || [];
  const collisions = (r && r.contact_id_collisions) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Contacts Collision Check (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Pennylane contact ids are numeric and scoped per company; db.contacts carries no field
          attributing a stored contact to the credential that wrote it, so two active credentials
          sharing a numeric id silently overwrite each other. Fetches live customers/suppliers per
          active credential (paginated, up to 1,000 per type) and cross-references ids. Read-only,
          no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm" data-testid="contacts-collision-run">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
          Run Collision Check
        </Button>

        <div className="border-t pt-4 space-y-3">
          <div>
            <p className="text-sm font-medium">Provenance backfill (legacy contacts)</p>
            <p className="text-xs text-muted-foreground">
              Stamps synced_credential_id/synced_entity_id on unstamped contact docs whose numeric id
              matches exactly one active credential's live list. Ids matching zero or multiple
              credentials are left unstamped and listed below. Preview is read-only; Apply writes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runBackfillPreview}
              disabled={previewLoading}
              size="sm"
              variant="outline"
              data-testid="contacts-backfill-preview"
            >
              {previewLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Preview Backfill
            </Button>
            <Button
              onClick={runBackfillApply}
              disabled={applyLoading || !backfillPreview}
              size="sm"
              data-testid="contacts-backfill-apply"
            >
              {applyLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply Backfill
            </Button>
          </div>

          {backfillPreview && !backfillApplyResult && (
            <div className="text-xs space-y-2" data-testid="contacts-backfill-preview-result">
              <p className="text-muted-foreground">Generated {backfillPreview.generated_at}</p>
              {backfillPreview.unstamped_docs_truncated && (
                <p className="text-amber-600">
                  More unstamped contact docs exist than this run considered; the plan below only
                  covers the first batch. Re-run after applying to cover the rest.
                </p>
              )}
              {(backfillPreview.credential_fetch_errors || []).length > 0 && (
                <p className="text-destructive">
                  {backfillPreview.credential_fetch_errors.length} credential fetch error(s) --
                  matched/ambiguous/unmatched below is computed on incomplete live data:{' '}
                  {backfillPreview.credential_fetch_errors.map((e) => e.entity_name).join(', ')}
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">{backfillPreview.matched_total} matched (single credential)</Badge>
                <Badge variant={backfillPreview.ambiguous_count > 0 ? 'destructive' : 'outline'}>
                  {backfillPreview.ambiguous_count} ambiguous
                </Badge>
                <Badge variant="outline">{backfillPreview.unmatched_count} unmatched</Badge>
              </div>
              {Object.keys(backfillPreview.matched_counts || {}).length > 0 && (
                <ul className="list-disc pl-4">
                  {Object.entries(backfillPreview.matched_counts).map(([credId, v]) => (
                    <li key={credId}>{v.entity_name}: {v.count} contact(s) to stamp</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {backfillApplyResult && (
            <div className="text-xs space-y-2" data-testid="contacts-backfill-apply-result">
              <p className="text-muted-foreground">Generated {backfillApplyResult.generated_at}</p>
              {backfillApplyResult.unstamped_docs_truncated && (
                <p className="text-amber-600">
                  More unstamped contact docs existed than this run considered; re-run Apply to
                  cover the rest.
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline">{backfillApplyResult.stamped_total} stamped</Badge>
                <Badge variant={backfillApplyResult.ambiguous_count > 0 ? 'destructive' : 'outline'}>
                  {backfillApplyResult.ambiguous_count} left ambiguous
                </Badge>
                <Badge variant="outline">{backfillApplyResult.unmatched_count} left unmatched</Badge>
                {backfillApplyResult.skipped_raced > 0 && (
                  <Badge variant="outline">{backfillApplyResult.skipped_raced} already stamped by a live sync</Badge>
                )}
              </div>
              {Object.keys(backfillApplyResult.per_credential || {}).length > 0 && (
                <ul className="list-disc pl-4">
                  {Object.entries(backfillApplyResult.per_credential).map(([credId, v]) => (
                    <li key={credId}>{v.entity_name}: {v.stamped} stamped</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {r && (
          <div className="space-y-4" data-testid="contacts-collision-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>Generated {r.generated_at}</span>
              <span>|</span>
              <span>{r.total_contacts} contacts docs</span>
              <span>|</span>
              <Badge variant={r.provenance_field_present ? 'outline' : 'destructive'}>
                {r.provenance_field_present
                  ? 'provenance field present'
                  : 'no provenance field on any contact doc'}
              </Badge>
              <span>|</span>
              <Badge variant={r.overlap_count > 0 ? 'destructive' : 'outline'}>
                {r.overlap_count} overlapping id(s)
              </Badge>
            </div>

            {r.insufficient_active_credentials_for_overlap_check && (
              <p className="text-sm text-amber-600">
                Fewer than 2 active Pennylane credentials actually produced live data on this run,
                so no meaningful overlap check was possible. {r.contacts_sync_wired_note}
              </p>
            )}

            {r.skipped_missing_id && (r.skipped_missing_id.customer > 0 || r.skipped_missing_id.supplier > 0) && (
              <p className="text-sm text-amber-600">
                Skipped {r.skipped_missing_id.customer} customer / {r.skipped_missing_id.supplier} supplier
                record(s) with no id in the live payload (excluded from the collision check, not
                coerced into a fabricated id).
              </p>
            )}

            <div>
              <p className="text-sm font-medium mb-1">Active credentials</p>
              <div className="flex flex-wrap gap-2">
                {(r.active_credentials || []).length === 0 && (
                  <span className="text-sm text-muted-foreground">None active.</span>
                )}
                {(r.active_credentials || []).map((c, i) => (
                  <Badge key={i} variant="outline">{c.entity_name}</Badge>
                ))}
              </div>
            </div>

            {(r.credential_fetch_errors || []).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1 text-destructive">Fetch errors</p>
                <ul className="text-xs text-destructive list-disc pl-4">
                  {r.credential_fetch_errors.map((e, i) => (
                    <li key={i}>{e.entity_name}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}

            {(r.credential_evidence || []).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">
                  Which credentials have ever written contacts (live evidence, docs carry no stamp)
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Credential</th>
                        <th className="text-right p-2">Customers fetched</th>
                        <th className="text-right p-2">Matched in db.contacts</th>
                        <th className="text-right p-2">Suppliers fetched</th>
                        <th className="text-right p-2">Matched in db.contacts</th>
                        <th className="text-center p-2">Likely contributed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.credential_evidence.map((e, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{e.entity_name}</td>
                          <td className="p-2 text-right font-mono">
                            {e.customers_fetched ?? '—'}{e.customers_possibly_truncated ? '+' : ''}
                          </td>
                          <td className="p-2 text-right font-mono">{e.customers_matched_in_db_contacts ?? '—'}</td>
                          <td className="p-2 text-right font-mono">
                            {e.suppliers_fetched ?? '—'}{e.suppliers_possibly_truncated ? '+' : ''}
                          </td>
                          <td className="p-2 text-right font-mono">{e.suppliers_matched_in_db_contacts ?? '—'}</td>
                          <td className="p-2 text-center">{yn(e.likely_has_contributed_to_db_contacts)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  "+" means the fetch hit the page cap (1,000) and more may exist beyond what was sampled.
                </p>
              </div>
            )}

            {overlaps.length === 0 ? (
              <p className="text-sm text-muted-foreground">No overlapping contact ids found across active credentials.</p>
            ) : (
              <div>
                <p className="text-sm font-medium mb-1">Overlapping ids (sample table)</p>
                {r.winner_attribution_caveat && (
                  <p className="text-xs text-muted-foreground mb-2">{r.winner_attribution_caveat}</p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">External id</th>
                        <th className="text-left p-2">Companies sharing this id (live name)</th>
                        <th className="text-left p-2">Stored name (current winner)</th>
                        <th className="text-left p-2">Currently matches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overlaps.map((o, i) => (
                        <tr key={i} className="border-t" data-testid="contacts-collision-row">
                          <td className="p-2">{o.type}</td>
                          <td className="p-2 font-mono">{o.external_id}</td>
                          <td className="p-2">
                            {o.holders.map((h) => `${h.entity_name} ("${h.live_name}")`).join(' vs ')}
                          </td>
                          <td className="p-2">{o.stored_name || '—'}</td>
                          <td className="p-2">{o.winner_entity_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-medium mb-1 flex items-center gap-2">
                Write-time collisions (the tripwire)
                <Badge variant={r.contact_id_collisions_count > 0 ? 'destructive' : 'outline'}>
                  {r.contact_id_collisions_count} recorded
                </Badge>
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Recorded by the live sync guard whenever a write would have overwritten a contact doc
                already stamped by a different credential, instead of silently overwriting it.
              </p>
              {collisions.length === 0 ? (
                <p className="text-sm text-muted-foreground">None recorded.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">External id</th>
                        <th className="text-left p-2">Existing owner (name)</th>
                        <th className="text-left p-2">Blocked writer (name)</th>
                        <th className="text-left p-2">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collisions.map((c, i) => (
                        <tr key={i} className="border-t" data-testid="contact-id-collision-row">
                          <td className="p-2">{c.type}</td>
                          <td className="p-2 font-mono">{c.external_id}</td>
                          <td className="p-2">{c.existing_entity_name || c.existing_entity_id} ("{c.existing_contact_name}")</td>
                          <td className="p-2">{c.incoming_entity_name || c.incoming_entity_id} ("{c.incoming_contact_name}")</td>
                          <td className="p-2">{c.timestamp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
